<template>
  <div
    ref="tableContainerRef"
    class="portfolio-table-container d-flex flex-column"
  >
    <!-- Header: page content, active-filter chips, column selector pinned top-right -->
    <div class="d-flex align-items-start gap-3 mb-2">
      <div
        v-if="$slots['header-start']"
        class="header-start d-flex align-items-center flex-shrink-0"
      >
        <slot name="header-start"></slot>
      </div>
      <PortfolioTableFilterChips
        :chips="tableFilters.chips"
        :trailingAction="tableFilters.trailingAction"
        @remove="tableFilters.clear([$event])"
        @reset="tableFilters.reset()"
      />
      <MultiSelect
        v-if="showColumnSelector"
        v-model="storedColumns"
        :options="columnOptions"
        optionLabel="label"
        optionValue="value"
        :placeholder="t('dashboard.portfolio.table.select_columns')"
        class="column-selector flex-shrink-0"
        display="chip"
        :focusOnHover="false"
      >
        <template #value="{ value }">
          <span class="text-muted">
            <font-awesome-icon icon="table-columns" class="me-2" />
            {{
              t("dashboard.portfolio.table.columns_selected", {
                count: value?.length ?? 0,
              })
            }}
          </span>
        </template>
      </MultiSelect>
    </div>

    <!-- Page messages about the filters, directly under the chips (Cancel's shortfall line). -->
    <slot name="below-filters"></slot>

    <!-- Positions the overlays; reveals clipped .text-truncate cells in full on hover -->
    <TruncatedTextReveal class="position-relative">
      <!-- PrimeVue DataTable with virtual scrolling for large datasets -->
      <DataTable
        ref="dt"
        v-model:selection="selectedItemsModel"
        :filters="tableFilters.primeFilters"
        @update:filters="tableFilters.applyPrimeFilters"
        :value="usedItems"
        dataKey="id"
        :selectionMode="disableSelection ? undefined : 'multiple'"
        :metaKeySelection="false"
        filterDisplay="row"
        class="portfolio-datatable border rounded"
        :rowClass="rowClass"
        :selectAll="selectAll"
        @select-all-change="onSelectAllChange"
        v-bind="virtualScrollProps"
        :pt="{ bodyRow: getBodyRowPT }"
        @filter="resetScroll"
        @sort="resetScroll"
      >
        <template #empty>
          <div class="text-center py-4 text-muted">
            {{ t("dashboard.portfolio.no_certificates_to_display") }}
          </div>
        </template>

        <!-- Selection Column -->
        <Column
          selectionMode="multiple"
          headerStyle="width: 1.5rem"
          v-if="!disableSelection"
        ></Column>

        <!-- Company Name (Conditional) -->
        <Column
          v-if="showCompanyColumn"
          field="company_name"
          :header="t('charts.fields.company_name')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: tableSize === 'compact' ? '8rem' : '10rem' }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            <div class="text-truncate">{{ data.company_name }}</div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter w-100"
              :placeholder="
                t('dashboard.portfolio.table.filters.search_company')
              "
            />
          </template>
        </Column>

        <!-- Account Emails (admin only) -->
        <Column
          v-if="isColumnVisible('account_emails')"
          field="account_emails"
          :header="t('charts.fields.account_emails')"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('account_emails') }"
        >
          <template #body="{ data }">
            <div v-if="data.account_emails?.length" class="text-truncate">
              {{ data.account_emails.join(", ") }}
            </div>
            <span v-else class="text-muted">—</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.account_emails"
              :placeholder="
                t('dashboard.portfolio.table.filters.search_accounts')
              "
              searchable
              mutedPlaceholder
              :tone="activeFilterTone('account_emails')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Count Column -->
        <Column
          v-if="isColumnVisible('count')"
          field="count"
          :header="t('charts.fields.count')"
          sortable
          dataType="numeric"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('count') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <!-- Fixed to the filtered total: selection lives in the action bar. -->
          <template #filter>
            <div class="d-flex justify-content-end">
              <div
                class="filtered-total d-flex flex-column align-items-end bg-primary-subtle rounded-2 p-1 text-nowrap"
              >
                <span
                  class="micro-label text-uppercase text-body-tertiary fw-semibold lh-1"
                >
                  {{ t("dashboard.portfolio.table.filtered_total") }}
                </span>
                <span class="filtered-total-value text-primary fw-bolder">
                  {{ formatNumberWithSpaces(filteredStats.totalMwh) }}
                </span>
              </div>
            </div>
          </template>
          <!-- PERF: rendered for every visible row, so no per-row watchers. Unselected rows (the bulk) stay a
               bare div; only selected rows mount a PortfolioCountCell. -->
          <template #body="{ data }">
            <PortfolioCountCell
              v-if="isCountEditable(data)"
              :count="data.count"
              :selected="selectedCount(data)"
              :editing="editingItemId === data.id"
              @edit="editingItemId = data.id"
              @commit="onCountCommit(data, $event)"
            />
            <div
              v-else-if="
                isPartialCount(data.selected_count ?? data.count, data)
              "
              class="d-flex flex-column align-items-end"
            >
              <span class="fw-semibold">{{
                formatNumberWithSpaces(data.selected_count)
              }}</span>
              <span class="text-nowrap text-body-tertiary small">
                {{
                  t("dashboard.portfolio.table.count_cell.of", {
                    count: formatNumberWithSpaces(data.count),
                  })
                }}
              </span>
            </div>
            <div v-else class="text-end">
              {{ formatNumberWithSpaces(data.count) }}
            </div>
          </template>
        </Column>

        <!-- Technology Type -->
        <Column
          v-if="isColumnVisible('technology_type')"
          field="technology_type"
          :header="t('charts.fields.technology_type')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('technology_type') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            {{ formatTechnology(data.technology_type) }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.technology_type"
              :tone="activeFilterTone('technology_type')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Production Month -->
        <Column
          v-if="isColumnVisible('production_month')"
          field="production_month"
          :header="t('charts.fields.production_month')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('production_month') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.production_month"
              searchable
              :tone="activeFilterTone('production_month')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Status -->
        <Column
          v-if="isColumnVisible('status')"
          field="status"
          :header="t('charts.fields.status')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('status') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            <span
              class="badge rounded-pill badge-compact"
              :class="getStatusBadgeClass(data.status)"
            >
              {{ formatStatus(data.status) }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.status"
              :tone="activeFilterTone('status')"
              @filter="filterCallback()"
            >
              <template #option="{ option }">
                <span
                  class="badge rounded-pill badge-compact"
                  :class="getStatusBadgeClass(String(option.value))"
                >
                  {{ option.label }}
                </span>
              </template>
            </ColumnMultiSelectFilter>
          </template>
        </Column>

        <!-- Portfolio Name -->
        <Column
          v-if="isColumnVisible('portfolio_name')"
          field="portfolio_name"
          :header="t('charts.fields.portfolio_name')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('portfolio_name') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            <div class="text-truncate">{{ formatPortfolio(data) }}</div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.portfolio_name"
              searchable
              :tone="activeFilterTone('portfolio_name')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Portfolio Type -->
        <Column
          v-if="isColumnVisible('portfolio_type')"
          field="portfolio_type"
          :header="t('charts.fields.portfolio_type')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('portfolio_type') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            {{
              t(
                `charts.values.portfolio_type.${data.portfolio_type}`,
                data.portfolio_type
              )
            }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.portfolio_type"
              :tone="activeFilterTone('portfolio_type')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Registration Authority -->
        <Column
          v-if="isColumnVisible('registration_authority')"
          field="registration_authority"
          :header="t('charts.fields.registration_authority')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('registration_authority') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            <div class="text-truncate">
              {{ data.registration_authority ?? "-" }}
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.registration_authority"
              searchable
              :tone="activeFilterTone('registration_authority')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Registry Region -->
        <Column
          v-if="isColumnVisible('registry_region')"
          field="registry_region"
          :header="t('charts.fields.registry_region')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('registry_region') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            <div class="text-truncate">
              {{ formatRegistryLabel(data.registry_region) }}
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.registry_region"
              searchable
              :tone="activeFilterTone('registry_region')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Issuing Region -->
        <Column
          v-if="isColumnVisible('issuing_region')"
          field="issuing_region"
          :header="t('charts.fields.issuing_region')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('issuing_region') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            <div class="text-truncate">
              {{ formatRegistryLabel(data.issuing_region) }}
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.issuing_region"
              searchable
              :tone="activeFilterTone('issuing_region')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Production Device -->
        <Column
          v-if="isColumnVisible('production_device_name')"
          field="production_device_name"
          :header="t('charts.fields.production_device_name')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('production_device_name') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            <div class="text-truncate">{{ data.production_device_name }}</div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DeviceGroupMultiSelect
              v-model="filterModel.value"
              class="p-column-filter"
              :options="tableFilters.options.production_device_name"
              :groups="deviceFilterGroups"
              :placeholder="t('common.filter')"
              :tone="activeFilterTone('production_device_name')"
              @change="filterCallback()"
            />
          </template>
        </Column>

        <!-- Production Support -->
        <Column
          v-if="isColumnVisible('production_support')"
          field="production_support"
          :header="t('charts.fields.production_support')"
          sortable
          dataType="boolean"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('production_support') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            {{ formatBoolean(data.production_support) }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.production_support"
              :tone="activeFilterTone('production_support')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Ecolabel Tags -->
        <Column
          v-if="isColumnVisible('ecolabel_tags')"
          field="ecolabel_tags"
          :header="t('charts.fields.ecolabel_tags')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('ecolabel_tags') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            <div v-if="data.ecolabel_tags.length" class="text-truncate">
              <span
                v-for="tag in data.ecolabel_tags"
                :key="tag"
                class="badge rounded-pill badge-compact me-1"
                :class="ecolabelBadgeClass(tag)"
              >
                {{ tag }}
              </span>
            </div>
            <span v-else>-</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.ecolabel_tags"
              :tone="activeFilterTone('ecolabel_tags')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- COD Age -->
        <Column
          v-if="isColumnVisible('cod_age')"
          field="cod_age"
          :header="t('charts.fields.cod_age')"
          sortable
          dataType="numeric"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('cod_age') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            {{ data.cod_age != null ? `${data.cod_age}yr` : "-" }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <CodAgeFilterEditor
              v-model="filterModel.value"
              :yearOptions="tableFilters.codAgeYearOptions"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Min Price -->
        <Column
          v-if="isColumnVisible('limit_price')"
          field="limit_price"
          :sortField="limitPriceSortField"
          :header="t('charts.fields.limit_price')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('limit_price') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            {{ data.limit_price ?? "-" }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.limit_price"
              :tone="activeFilterTone('limit_price')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Sale Price -->
        <Column
          v-if="isColumnVisible('sale_price')"
          field="sale_price"
          :sortField="salePriceSortField"
          :header="t('charts.fields.sale_price')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('sale_price') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            {{ data.sale_price != null ? `${data.sale_price} €` : "-" }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.sale_price"
              :tone="activeFilterTone('sale_price')"
              @filter="filterCallback()"
            />
          </template>
        </Column>

        <!-- Registry ID (admin only) -->
        <Column
          v-if="isColumnVisible('registry_id')"
          field="registry_id"
          :header="t('charts.fields.registry_id')"
          sortable
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('registry_id') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            <div v-if="data.registry_id" class="text-truncate">
              {{ data.registry_id }}
            </div>
            <span v-else class="text-muted">-</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter w-100"
              :placeholder="t('common.filter')"
            />
          </template>
        </Column>

        <!-- Bundle Start (admin only) -->
        <Column
          v-if="isColumnVisible('bundle_start')"
          field="bundle_start"
          :header="t('charts.fields.bundle_start')"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('bundle_start') }"
        >
          <template #body="{ data }">
            <div v-if="data.bundle_start" class="text-truncate">
              {{ data.bundle_start }}
            </div>
            <span v-else class="text-muted">-</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter w-100"
              :placeholder="t('common.filter')"
            />
          </template>
        </Column>

        <!-- Bundle End (admin only) -->
        <Column
          v-if="isColumnVisible('bundle_end')"
          field="bundle_end"
          :header="t('charts.fields.bundle_end')"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('bundle_end') }"
        >
          <template #body="{ data }">
            <div v-if="data.bundle_end" class="text-truncate">
              {{ data.bundle_end }}
            </div>
            <span v-else class="text-muted">-</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              class="p-column-filter w-100"
              :placeholder="t('common.filter')"
            />
          </template>
        </Column>

        <!-- Transfer Deadline -->
        <Column
          v-if="isColumnVisible('transfer_deadline')"
          field="transfer_deadline"
          :header="t('charts.fields.transfer_deadline')"
          sortable
          dataType="date"
          :showFilterMatchModes="false"
          :showFilterMenu="false"
          :headerStyle="{ width: getColumnWidth('transfer_deadline') }"
        >
          <template #sorticon="{ sortOrder }">
            <ColumnSortIcon :sortOrder />
          </template>
          <template #body="{ data }">
            {{ formatDate(data.transfer_deadline) }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <ColumnMultiSelectFilter
              v-model="filterModel.value"
              :options="tableFilters.options.transfer_deadline"
              searchable
              :tone="activeFilterTone('transfer_deadline')"
              @filter="filterCallback()"
            />
          </template>
        </Column>
      </DataTable>

      <ScrollMoreIndicator
        :scrollTarget="scrollElement"
        :itemHeight="ROW_HEIGHT_PX"
      >
        <template #default="{ count }">
          ~{{
            t("dashboard.portfolio.table.more_certificates", {
              count,
            })
          }}
        </template>
      </ScrollMoreIndicator>
    </TruncatedTextReveal>

    <!-- Rendered wherever the action bar can appear, before any selection, so assistive tech
         announces the first selection and its clearing too. Read-only and hide-action-bar tables
         have none: a page that shares one selection between tables would announce it twice. -->
    <span
      v-if="!hideActionBar && !disableSelection"
      class="visually-hidden"
      role="status"
      >{{ selectionAnnouncement }}</span
    >

    <!-- Static Action Footer (Always below table) -->
    <div
      ref="staticFooterRef"
      v-if="hasSelectedItems && !hideActionBar && !disableSelection"
      class="mt-2"
    >
      <PortfolioTableActionBar
        :selection
        :availableMwh="filteredStats.availableMwh"
        :hiddenRowsCount="deselectHiddenOnFilterChange ? 0 : hiddenRowsCount"
        :showPartialHint="showPartialAmountHint && enableCountEditing"
      >
        <template #details v-if="$slots['footer-details']">
          <slot name="footer-details"></slot>
        </template>
        <template #action>
          <slot name="footer-action">
            <!-- Default Overview Action Dropdown -->
            <PortfolioTableActionDropdown />
          </slot>
        </template>
      </PortfolioTableActionBar>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ecolabelBadgeClass } from "@/utils/ecolabelBadges";
import ScrollMoreIndicator from "@/components/common/ScrollMoreIndicator.vue";
import TruncatedTextReveal from "@/components/common/TruncatedTextReveal.vue";
import ColumnMultiSelectFilter from "@/components/common/ColumnMultiSelectFilter.vue";
import {
  formatDateTimeIntoDate,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import {
  useVirtualScrolledTable,
  type VirtualScrolledTable,
} from "@/composables/useVirtualScrolledTable";
import CodAgeFilterEditor from "@/dashboard/inventory_management/common/CodAgeFilterEditor.vue";
import PortfolioCountCell from "@/dashboard/inventory_management/common/PortfolioCountCell.vue";
import {
  allItemsSelectable,
  isPartialCount,
} from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import PortfolioTableActionBar from "@/dashboard/inventory_management/common/PortfolioTableActionBar.vue";
import PortfolioTableActionDropdown from "@/dashboard/inventory_management/common/PortfolioTableActionDropdown.vue";
import PortfolioTableFilterChips from "@/dashboard/inventory_management/common/PortfolioTableFilterChips.vue";
import {
  ALL_COLUMNS,
  ColumnKey,
  DEFAULT_COLUMNS,
  type SelectableFilterColumn,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import type {
  FilterColumn,
  FilterScalar,
  FilterState,
} from "@/dashboard/inventory_management/common/portfolioTableFilters";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import {
  type PortfolioSelection,
  usePortfolioSelection,
} from "@/dashboard/inventory_management/common/usePortfolioSelection";
import { usePortfolioTableFilters } from "@/dashboard/inventory_management/common/usePortfolioTableFilters";
import { buildDeviceFilterGroups } from "@/dashboard/inventory_management/common/portfolioFilterOptions";
import DeviceGroupMultiSelect from "@/dashboard/production_strategy/device_groups/DeviceGroupMultiSelect.vue";
import { FilteredPortfolioStats, FlattenedPortfolioItem } from "@/types";
import { getFlagEmoji } from "@/utils/countries";
import { getRegistryLabel } from "@/utils/registries";
import { useLocalStorage } from "@vueuse/core";
import { sumBy } from "lodash-es";
import Column from "primevue/column";
import type { DataTablePassThroughMethodOptions } from "primevue/datatable";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  toRef,
  useTemplateRef,
  watch,
  watchPostEffect,
} from "vue";
import { useI18n } from "vue-i18n";
import ColumnSortIcon from "@/components/common/ColumnSortIcon.vue";
import { translateTechnology } from "@/utils/technologies";

const props = withDefaults(
  defineProps<{
    /**
     * Required when `disableSelection` is false. May be omitted in read-only
     * mode (`disableSelection`), in which case a stub selection is manufactured
     * locally.
     */
    selection?: PortfolioSelection;
    enableCountEditing?: boolean;
    disableSelection?: boolean;
    allowMultiCompanySelection?: boolean;
    /**
     * When no `items` prop is passed, controls which cache set to render:
     *   true  → cache.allItems  (admin-style, shows PENDING/RESERVED/etc.)
     *   false → cache.availableItems
     * Selection-policy decisions come from `selection.isSelectable`, not this flag.
     */
    isForAdmin?: boolean;
    unselectablePortfolioTypes?: Set<string>;
    items?: FlattenedPortfolioItem[];
    defaultVisibleColumns?: ColumnKey[];
    /**
     * The only columns this table renders or offers in its column selector, whatever the stored
     * column choice says; all columns when unset. An allowlist, so a view whose rows must not
     * carry some data (a buyer's view of Soldera's inventory) never gains a column by default —
     * the company column included, which no allowlist names.
     */
    availableColumns?: ColumnKey[];
    showColumnSelector?: boolean;
    storageContext?: string;
    tableSize?: "default" | "compact";
    hideActionBar?: boolean;
    /** Initial portfolio_type include-list; shown as the column's Default filter. */
    defaultPortfolioTypeFilter?: string[];
    /**
     * Values each column hides by default, resolved against the options present in the data: the
     * default include-list is the present options minus these. A column none of whose excluded
     * values are present gets no default filter.
     */
    defaultExcludedFilterValues?: Partial<
      Record<SelectableFilterColumn, string[]>
    >;
    /**
     * Deselect the selected rows a filter change hides, keeping visible rows and their amounts.
     * Off by default: other pages keep selections made under other filters.
     */
    deselectHiddenOnFilterChange?: boolean;
    /** Query param that persists the filters; unset keeps them in memory only. */
    filterUrlParam?: string;
    /** Identifies the data the persisted filters belong to (e.g. the company ids). */
    filterUrlScope?: string;
    /** Show "Click an amount to select part of a row" in the action bar while no row is partial. */
    showPartialAmountHint?: boolean;
  }>(),
  {
    enableCountEditing: false,
    disableSelection: false,
    allowMultiCompanySelection: false,
    isForAdmin: false,
    unselectablePortfolioTypes: undefined,
    items: undefined,
    defaultVisibleColumns: () => DEFAULT_COLUMNS,
    availableColumns: undefined,
    showColumnSelector: true,
    storageContext: "default",
    tableSize: "default",
    hideActionBar: false,
    defaultPortfolioTypeFilter: undefined,
    defaultExcludedFilterValues: undefined,
    deselectHiddenOnFilterChange: false,
    filterUrlParam: undefined,
    filterUrlScope: "",
    showPartialAmountHint: false,
  }
);

// NOTE: `selection` is required for interactive tables. A read-only table (`disableSelection`) may omit
// it and gets a stub that treats every row as selectable, so no row is greyed out as unselectable.
const selection: PortfolioSelection =
  props.selection ??
  (props.disableSelection
    ? usePortfolioSelection({ policy: allItemsSelectable })
    : (() => {
        throw new Error(
          "PortfolioItemsTable: `selection` prop is required when `disableSelection` is false"
        );
      })());

const emit = defineEmits<{
  "update:filteredStats": [stats: FilteredPortfolioStats];
}>();

const portfolioItemsStore = usePortfolioItemsStore();
const { t } = useI18n();

// Column visibility with localStorage persistence per context
const storageKey = computed(
  () => `portfolio-table-columns-${props.storageContext}`
);

const validColumnKeys = new Set(Object.keys(ALL_COLUMNS));
const isValidColumn = validColumnKeys.has.bind(validColumnKeys);

interface ColumnStorage {
  visible: ColumnKey[];
  seen: ColumnKey[];
}

const defaultColumnStorage = (): ColumnStorage => ({
  visible: props.defaultVisibleColumns,
  seen: [...props.defaultVisibleColumns],
});

// Reconcile stored columns with current column definitions.
// Storage format: {visible: [...], seen: [...]} where "seen" tracks all columns
// the user has encountered (including ones they deliberately hid).
// New default columns (not in "seen") are auto-appended to "visible".
// Deliberately hidden columns (in "seen" but not "visible") stay hidden.
const readColumnStorage = (raw: string): ColumnStorage => {
  try {
    const parsed = JSON.parse(raw);
    // Migrate from old format (plain array) to new {visible, seen} format
    const data: ColumnStorage = Array.isArray(parsed)
      ? { visible: parsed, seen: parsed }
      : parsed;
    const visible = data.visible.filter(isValidColumn) as ColumnKey[];
    const seen = data.seen.filter(isValidColumn) as ColumnKey[];
    if (visible.length === 0 && seen.length === 0)
      return defaultColumnStorage();
    const newDefaults = props.defaultVisibleColumns.filter(
      (k) => !seen.includes(k)
    );
    return {
      visible: [...visible, ...newDefaults],
      seen: [...seen, ...newDefaults],
    };
  } catch {
    return defaultColumnStorage();
  }
};

const columnStorage = useLocalStorage<ColumnStorage>(
  storageKey,
  defaultColumnStorage(),
  {
    serializer: {
      read: readColumnStorage,
      write: (value: ColumnStorage): string => JSON.stringify(value),
    },
  }
);

// Computed wrapper so v-model on the column selector reads/writes "visible",
// while "seen" is kept in sync with all known column keys.
const storedColumns = computed({
  get: () => columnStorage.value.visible,
  set: (value: ColumnKey[]) => {
    const allKnown = new Set([...columnStorage.value.seen, ...value]);
    columnStorage.value = {
      visible: value,
      seen: [...allKnown] as ColumnKey[],
    };
  },
});

// Only admins can pick these, and only admins see them even when stored as visible.
const ADMIN_ONLY_COLUMNS: ReadonlySet<ColumnKey> = new Set([
  "account_emails",
  "registry_id",
  "bundle_start",
  "bundle_end",
]);

const isColumnAvailable = (field: ColumnKey): boolean =>
  (props.isForAdmin || !ADMIN_ONLY_COLUMNS.has(field)) &&
  (props.availableColumns === undefined ||
    props.availableColumns.includes(field));

const columnOptions = computed(() =>
  Object.values(ALL_COLUMNS)
    .filter((col) => isColumnAvailable(col.field))
    .map((col) => ({
      label: t(col.header),
      value: col.field,
    }))
);

const isColumnVisible = (field: ColumnKey) =>
  isColumnAvailable(field) && storedColumns.value.includes(field);

const getColumnWidth = (field: ColumnKey) => {
  const col = ALL_COLUMNS[field];
  return props.tableSize === "compact" ? col.width.compact : col.width.default;
};

// DOM refs
const tableContainerRef = useTemplateRef<HTMLElement>("tableContainerRef");
const staticFooterRef = useTemplateRef<HTMLElement>("staticFooterRef");

// The virtual scroller places row n at n × itemSize and never measures a row, so every row must
// render exactly this tall: the style block pins it, and cells truncate on one line instead of
// wrapping. Tall enough for the two-line partial count cell and its editor.
const ROW_HEIGHT_PX = 48;

// Rows the table renders: the caller's `items` when given, otherwise the store slice. Label maps
// and filter options below derive from these rows so injected rows get correct options too.
const usedItems = computed(() => {
  const rawItems =
    props.items ??
    (props.isForAdmin
      ? portfolioItemsStore.allItems
      : portfolioItemsStore.availableItems);
  const items = Array.isArray(rawItems) ? rawItems : [];
  return items.map((item) => ({
    ...item,
    ecolabel_tags: [
      ...(item.ecolabel_approved ? ["ekoenergy"] : []),
      ...(item.okpower_new_compatible ? ["okpower-new"] : []),
    ],
  }));
});

// PERF: Pre-compute translation maps once to avoid calling t() for every cell render
const technologyLabels = computed(() => {
  const map = new Map<string, string>();
  for (const item of usedItems.value) {
    const val = item.technology_type;
    if (val && !map.has(val)) {
      map.set(val, translateTechnology(val, t));
    }
  }
  return map;
});

const statusLabels = computed(() => {
  const map = new Map<string, string>();
  for (const item of usedItems.value) {
    const val = item.status;
    if (val && !map.has(val)) {
      map.set(val, t(`charts.values.certificate_status.${val}`, val));
    }
  }
  return map;
});

const portfolioLabels = computed(() => {
  const map = new Map<string, string>();
  for (const item of usedItems.value) {
    const key = `${item.portfolio_type}:${item.portfolio_name ?? ""}`;
    if (!map.has(key)) {
      if (item.portfolio_type === "VIRTUAL" || item.portfolio_type === "PPA") {
        map.set(key, item.portfolio_name ?? "-");
      } else {
        map.set(
          key,
          t(
            `charts.values.portfolio_type.${item.portfolio_type}`,
            item.portfolio_name
          )
        );
      }
    }
  }
  return map;
});

const dateLabels = computed(() => {
  const map = new Map<string, string>();
  for (const item of usedItems.value) {
    const val = item.transfer_deadline;
    if (val && !map.has(val)) {
      map.set(val, formatDateTimeIntoDate(val));
    }
  }
  return map;
});

const registryLabels = computed(() => {
  const map = new Map<string, string>();
  for (const item of usedItems.value) {
    for (const val of [item.registry_region, item.issuing_region]) {
      if (val && !map.has(val)) {
        const label = getRegistryLabel(val, t);
        const iso2 = val.substring(0, 2);
        const flag = getFlagEmoji(iso2);
        map.set(val, flag ? `${flag} ${label}` : label);
      }
    }
  }
  return map;
});

// PERF: Fast lookup functions that use pre-computed maps
const formatTechnology = (val: string | null | undefined): string =>
  val ? (technologyLabels.value.get(val) ?? val) : "-";

const formatStatus = (val: string | null | undefined): string =>
  val ? (statusLabels.value.get(val) ?? val) : "-";

const formatPortfolio = (item: FlattenedPortfolioItem): string => {
  const key = `${item.portfolio_type}:${item.portfolio_name ?? ""}`;
  return portfolioLabels.value.get(key) ?? "-";
};

const formatDate = (val: string | null | undefined): string =>
  val ? (dateLabels.value.get(val) ?? formatDateTimeIntoDate(val)) : "-";

// null means the registry gave us no support data (e.g. imported holdings) — not "No"
const formatBoolean = (val: boolean | null | undefined): string =>
  val == null ? t("common.unknown") : val ? t("common.yes") : t("common.no");

const formatRegistryLabel = (
  registryRegion: string | null | undefined
): string => {
  if (!registryRegion) return "-";
  return registryLabels.value.get(registryRegion) ?? registryRegion;
};

// Data preparation
const isSelectable = (item: FlattenedPortfolioItem) => {
  if (props.unselectablePortfolioTypes?.has(item.portfolio_type)) return false;
  return selection.isSelectable(item);
};

const hasMultipleCompanies = computed(() => {
  const items = usedItems.value;
  if (items.length < 2) return false;
  const firstCompanyId = items[0].company_id;
  return items.some((item) => item.company_id !== firstCompanyId);
});

const showCompanyColumn = computed(
  () =>
    props.availableColumns === undefined &&
    (hasMultipleCompanies.value ||
      portfolioItemsStore.sourceHasMultipleCompanies)
);

const disableSelection = computed(
  () =>
    props.disableSelection ||
    (!props.allowMultiCompanySelection && hasMultipleCompanies.value)
);

// Selection Model (Two-way binding with the selection instance)
const selectedItemsModel = computed({
  get: () => selection.selectedItems,
  set: (val) => {
    selection.setSelectedItems(val.filter((i) => isSelectable(i)));
  },
});

const hasSelectedItems = computed(() => selectedItemsModel.value.length > 0);

// PrimeVue exposes `processedData` (the rows its filters and sort leave) at runtime but not in its types.
type PortfolioDataTable = VirtualScrolledTable & {
  processedData: FlattenedPortfolioItem[];
};
const dt = useTemplateRef<PortfolioDataTable>("dt");

// Past 14 rows only the rows in view render, in a viewport that fits the rows the filters leave
// and scrolls past 600px.
const {
  tableProps: virtualScrollProps,
  resetScroll,
  scrollElement,
} = useVirtualScrolledTable(dt, () => usedItems.value.length, ROW_HEIGHT_PX);

// The rows the filters leave, read from the DataTable (its cached `processedData`). The same rows in
// another order keep the previous array: a sort must not re-emit stats, since Cancel's auto-select
// breaks ties by input order. A refetch builds new row objects, so it still yields a new array.
const visibleItems = computed<FlattenedPortfolioItem[]>((previous) => {
  const rows = dt.value?.processedData ?? usedItems.value;
  if (previous?.length !== rows.length) return rows;
  const previousRows = new Set(previous);
  return rows.every((row) => previousRows.has(row)) ? previous : rows;
});

const visibleSelectable = computed(() =>
  visibleItems.value.filter(isSelectable)
);

// NOTE: a row whose `selected_count` is below `count` displays that share, so the totals sum shares.
const rowMwh = (item: FlattenedPortfolioItem): number =>
  item.selected_count ?? item.count;

const filteredStats = computed((): FilteredPortfolioStats => ({
  count: visibleItems.value.length,
  totalMwh: sumBy(visibleItems.value, rowMwh),
  availableMwh: sumBy(visibleSelectable.value, rowMwh),
  items: visibleItems.value,
}));

const hiddenRowsCount = computed(() => {
  const visibleIds = new Set(visibleItems.value.map((item) => item.id));
  return selection.selectedItems.filter((item) => !visibleIds.has(item.id))
    .length;
});

// Built from the selection alone, so typing in a filter doesn't trigger announcements.
const selectionAnnouncement = computed(() =>
  hasSelectedItems.value
    ? `${t("dashboard.portfolio.footer.rows_selected", selection.selectedItems.length)}, ${t(
        "dashboard.portfolio.footer.selected_mwh",
        { mwh: formatNumberWithSpaces(selection.totalSelectedMwh) }
      )}`
    : t("dashboard.portfolio.footer.no_certificates_selected")
);

// Row Class Logic
const rowClass = (data: FlattenedPortfolioItem) => {
  // PrimeVue expects a string (or array) here; returning an object does not reliably apply to the <tr>.
  return isSelectable(data) ? "" : "row-disabled";
};

const getDisabledReason = (item: FlattenedPortfolioItem): string => {
  if (props.unselectablePortfolioTypes?.has(item.portfolio_type)) {
    return t("dashboard.portfolio.disabled_reason_portfolio", {
      portfolio: t(
        `charts.values.portfolio_type.${item.portfolio_type}`,
        item.portfolio_type
      ),
    });
  }
  const status = item.status;
  let key = "dashboard.portfolio.disabled_reason_unknown";
  let params = {};
  if (status === "SOLD")
    key = "dashboard.portfolio.disabled_reason_status_sold";
  else if (status === "ONGOING_SALE")
    key = "dashboard.portfolio.disabled_reason_status_ongoing_sale";
  else if (status === "EXPIRED")
    key = "dashboard.portfolio.disabled_reason_status_expired";
  else if (status === "NON_TRANSFERRABLE")
    key = "dashboard.portfolio.disabled_reason_status_non_transferrable";
  else if (status === "OWN_USE")
    key = "dashboard.portfolio.disabled_reason_status_own_use";
  else if (status === "RESERVED")
    key = "dashboard.portfolio.disabled_reason_status_reserved";
  else if (status === "WAITING_FOR_SALE")
    key = "dashboard.portfolio.disabled_reason_status_waiting_for_sale";
  else if (status === "PENDING")
    key = "dashboard.portfolio.disabled_reason_status_pending";
  else if (status && status !== "AVAILABLE") {
    key = "dashboard.portfolio.disabled_reason_status_unknown";
    params = { status: item.status };
  }
  return t(key, params);
};

const selectAll = computed(
  () =>
    !disableSelection.value &&
    visibleSelectable.value.length > 0 &&
    visibleSelectable.value.every((item) =>
      selection.selectedItemIds.has(item.id)
    )
);

/**
 * The rows the filters currently leave visible, in the order the table shows them. Read live rather
 * than from `visibleItems`, which keeps its pre-sort order: callers such as the admin auto-select
 * break ties by this order.
 */
const getFilteredItems = (): FlattenedPortfolioItem[] =>
  dt.value?.processedData ?? usedItems.value;

// Select All is scoped to the currently filtered rows: checking adds visible
// selectable items to the existing selection, unchecking removes only those.
// Selections made under other filters are preserved so the user can build up
// a selection across multiple filter passes.
const onSelectAllChange = (event: { checked: boolean }) => {
  if (event.checked) {
    for (const item of visibleSelectable.value) {
      selection.selectItem(item.id);
    }
  } else {
    for (const item of visibleItems.value) {
      selection.deselectItem(item.id);
    }
  }
};

// Count editing ("2A"): ticking a row takes it in full; clicking the selected amount opens an editor
// for a partial amount. Only one row edits at a time, and it commits on Enter, Escape or blur.
const editingItemId = ref<number | null>(null);

const isCountEditable = (item: FlattenedPortfolioItem) =>
  props.enableCountEditing &&
  !disableSelection.value &&
  selection.getSelectedCount(item.id) !== undefined;

// Only called for rows isCountEditable passes, which are selected; the fallback just keeps it a number.
const selectedCount = (item: FlattenedPortfolioItem) =>
  selection.getSelectedCount(item.id) ?? item.count;

// Empty or ≤ 0 unchecks the row; anything else is the new amount, which the selection clamps to
// [1, count] (≥ count takes the row in full again). The guard drops a second blur after Enter/Escape.
const onCountCommit = (item: FlattenedPortfolioItem, count: number | null) => {
  if (editingItemId.value !== item.id) return;
  editingItemId.value = null;
  if (count === null || count <= 0) {
    selection.deselectItem(item.id);
  } else {
    selection.updateItemSelectedCount(item.id, count);
  }
};

// Filter options, defaults, the user's overrides, chips and URL persistence. The options derive from
// the rows the table renders and its label maps, so injected rows get correct options too.
const tableFilters = usePortfolioTableFilters({
  items: () => usedItems.value,
  labels: () => ({
    technology: technologyLabels.value,
    status: statusLabels.value,
    transferDeadline: dateLabels.value,
    registry: registryLabels.value,
  }),
  t,
  defaultPortfolioTypes: () => props.defaultPortfolioTypeFilter,
  excludedByDefault: () => props.defaultExcludedFilterValues,
  persist: props.filterUrlParam
    ? { param: props.filterUrlParam, scope: () => props.filterUrlScope }
    : undefined,
});

const deviceFilterGroups = computed(() =>
  buildDeviceFilterGroups(usedItems.value)
);

const activeFilterTone = (column: FilterColumn) =>
  tableFilters.summary.unavailableColumns.includes(column)
    ? "danger"
    : "primary";

// Styling helpers
const getStatusBadgeClass = (status: string | undefined) => {
  if (status === "AVAILABLE") return "bg-success-subtle text-success-emphasis";
  if (status === "RESERVED") return "bg-warning-subtle text-warning-emphasis";
  if (status === "SOLD") return "bg-secondary text-white";
  if (status === "ONGOING_SALE") return "bg-info-subtle text-info-emphasis";
  return "bg-secondary-subtle text-secondary-emphasis";
};

const limitPriceSortField = ((item: FlattenedPortfolioItem) =>
  item.limit_price != null ? parseFloat(item.limit_price) : -1) as unknown as (
  item: any
) => string;

const salePriceSortField = ((item: FlattenedPortfolioItem) =>
  item.sale_price != null ? parseFloat(item.sale_price) : -1) as unknown as (
  item: any
) => string;

onMounted(() => {
  // Prevent selecting rows that are not available. PrimeVue's DataTable does not have
  // a first-class "row disabled" selection hook, so we block click events in capture phase
  // before DataTable's internal row/checkbox handlers run.
  tableContainerRef.value?.addEventListener("click", onTableClickCapture, true);
  // The initial (default or URL-restored) filters count as a filter change. Template refs are set by
  // now, so the visible rows already come from the DataTable.
  if (props.deselectHiddenOnFilterChange) deselectHiddenRows();
});

// Post-render, so the first run reads the mounted DataTable rather than the unfiltered rows. Re-emits
// on any new item set, including a same-size refetch.
watchPostEffect(() => emit("update:filteredStats", filteredStats.value));

// One batched update that keeps visible selected rows with their amounts (setSelectedItems leaves
// the count of an already-selected row alone, and triggers nothing when no row is hidden).
const deselectHiddenRows = () => {
  selection.setSelectedItems(
    visibleItems.value.filter((item) => selection.selectedItemIds.has(item.id))
  );
};

// A filter change deselects the rows it hides. A change in the items themselves (a refetch, which may
// also re-resolve a default filter in the same flush) never deselects.
watch(
  [visibleItems, usedItems],
  (_, [, previousItems]) => {
    if (
      props.deselectHiddenOnFilterChange &&
      usedItems.value === previousItems
    ) {
      deselectHiddenRows();
    }
  },
  { flush: "post" }
);

onUnmounted(() => {
  tableContainerRef.value?.removeEventListener(
    "click",
    onTableClickCapture,
    true
  );
});

const onTableClickCapture = (event: Event) => {
  const target = event.target as HTMLElement | null;
  if (!target) return;

  // Block both row-click selection and checkbox selection for disabled rows.
  // Also prevents "select-all" range behavior from including unavailable rows via clicks.
  const disabledRow = target.closest("tr.row-disabled");
  if (!disabledRow) return;

  event.preventDefault();
  event.stopPropagation();

  // stopImmediatePropagation is not on Event type in TS, but exists on native events.
  const maybeStopImmediatePropagation = event as unknown as {
    stopImmediatePropagation?: () => void;
  };
  maybeStopImmediatePropagation.stopImmediatePropagation?.();
};

// Extended type for bodyRow PT options which includes rowData in props
interface BodyRowPTOptions extends DataTablePassThroughMethodOptions {
  props: DataTablePassThroughMethodOptions["props"] & {
    rowData?: FlattenedPortfolioItem;
  };
}

/**
 * Pass Through function for body rows to add tooltip to disabled rows
 */
const getBodyRowPT = (options: BodyRowPTOptions) => {
  const item = options.props.rowData;

  if (!item) {
    return {};
  }

  if (!isSelectable(item)) {
    return {
      title: getDisabledReason(item),
    };
  }
  return {};
};

/** Filter a list column to exactly these values; `[]` clears it. Same path as its filter editor. */
const setColumnFilter = (
  column: SelectableFilterColumn,
  values: FilterScalar[]
) => {
  const partial: Partial<FilterState> = {};
  partial[column] = values;
  tableFilters.set(partial);
};

const getFilterOptions = () => tableFilters.options;

defineExpose({
  getFilteredItems,
  getFilterOptions,
  /** Every column's current filter value, in column order. */
  filterValues: toRef(tableFilters, "values"),
  setColumnFilter,
  /** Clear the given columns' filters, leaving the others alone. */
  clearFilters: tableFilters.clear,
  /**
   * Set the given columns' filters (e.g. a document's criteria), leaving the others alone. Each
   * column gets its own copy, so the caller's values stay independent of the editors.
   */
  applyColumnFilters: tableFilters.set,
  /**
   * Replace the document's filters (`null` drops them): clears the previous document's columns that
   * still hold its values, then applies the new ones, whose chips carry the "From document" tag.
   */
  setDocumentFilters: tableFilters.setDocument,
  filterSummary: toRef(tableFilters, "summary"),
});
</script>

<style scoped lang="scss">
.portfolio-table-container {
  overflow-x: hidden;
}

.portfolio-datatable :deep(.pv-datatable-body-row) {
  height: v-bind("`${ROW_HEIGHT_PX}px`");
}

// Column selector styling
.column-selector {
  min-width: 160px;
  max-width: 220px;
}

// Compact status badge
.badge-compact {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  font-weight: 600;
}

// Count filter cell: the filtered total, never the selection.
.filtered-total {
  gap: 1px;
}

.filtered-total-value {
  font-size: 0.8125rem;
  line-height: 1.1;
}

// Keeps page content in the header (titles, toggles) centred on the column selector's first line.
.header-start {
  min-height: 36px;
}
</style>
